import { el, clear } from "../components/dom.js";
import { persistedState } from "../utils/list-prefs.js";
import { bulkSelect } from "../components/bulk-select.js";
import { randomPickButton } from "../components/random-pick.js";
import { OWNERSHIP_STATUSES } from "../components/status-badge.js";
import { filterBar } from "../components/filter-bar.js";
import { groupToggle } from "../components/group-toggle.js";
import { parseFilters, matchesAllFilters } from "../utils/filters.js";
import { listMovies } from "../db/movies.js";
import { listGroups, categoryGroups } from "../db/groups.js";
import { listAllMedia } from "../db/media.js";
import { getRankingState } from "../db/ranking.js";
import { enableRowDrag } from "../utils/drag-drop.js";
import { sortedEntries } from "../utils/group-sort.js";
import { handleRowDrop } from "./group-dnd.js";
import { renderCategoryEntries } from "../components/mag-gallery.js";
import { viewToggle } from "../components/view-toggle.js";
import { getListView, setListView } from "../utils/view-mode.js";
import { navigate, navigateBack, getQueryParams } from "../main.js";
import { saveScroll, consumeScroll } from "../utils/scroll.js";

// The Movies category list — the TV list plus the anime list's genre facet,
// built from the OMDb genres stored on each film.

const SCROLL_KEY = "movies-list";
const EMPTY_IDS = new Set(); // shared "collapse nothing" set for ungrouped view

const SORTS = [
	{ id: "created_at", label: "Date added" },
	{ id: "title", label: "Title A–Z" },
	{ id: "release_year", label: "Release year" },
	{ id: "last_engaged_date", label: "Last watched" },
	{ id: "rating", label: "Rating" },
	{ id: "battle_rank", label: "Battle rank" },
];

const state = persistedState("movies-list", {
	sort: "created_at",
	// "gallery" (masonry tiles) or "list" (glass rows) — mirrors the device-wide
	// setting in utils/view-mode.js; re-read every time the screen renders.
	view: "gallery",
	genre: "", // "" = all genres
	// false = collections show as group cards (members collapsed); true = groups
	// are dissolved and every member shows as its own item in the list.
	expandGroups: false,
});

function tagsOf(m) {
	return m.genres ?? [];
}

export async function renderMoviesList(root) {
	const header = el(
		"header.app-header",
		{},
		el(
			"button.app-header__back",
			{ type: "button", onclick: () => navigateBack("/") },
			"‹",
		),
		el("h1", {}, "Movies"),
		el(
			"button.app-header__icon",
			{ type: "button", title: "VS Battle", onclick: () => navigate("/movies/vs") },
			"VS",
		),
		randomPickButton("movie"),
		el(
			"button.app-header__action",
			{ type: "button", onclick: () => navigate("/movies/add") },
			"+ Add",
		),
	);

	const sortSelect = el(
		"select.toolbar__sort",
		{
			value: state.sort,
			onchange: (e) => {
				state.sort = e.currentTarget.value;
				renderList();
			},
		},
		...SORTS.map((s) =>
			el("option", { value: s.id, selected: state.sort === s.id }, s.label),
		),
	);

	// Genre facet — built from the genres OMDb put on each film in the library.
	const genreSelect = el("select.toolbar__sort", {
		onchange: (e) => {
			state.genre = e.currentTarget.value;
			renderList();
		},
	});

	const filter = filterBar({
		mediaType: "movie",
		ownershipStatuses: OWNERSHIP_STATUSES,
		initial: parseFilters(getQueryParams()),
		onChange: () => renderList(),
	});

	// Grouped ⇄ ungrouped view toggle (hidden when there are no groups).
	const groupBtn = groupToggle({
		expanded: state.expandGroups,
		onChange: (expanded) => {
			state.expandGroups = expanded;
			renderList();
		},
	});

	// Gallery ⇄ list layout toggle — one device-wide setting shared by every
	// category list (utils/view-mode.js).
	state.view = getListView();
	const viewBtn = viewToggle({
		mode: state.view,
		onChange: (mode) => {
			state.view = setListView(mode);
			renderList();
		},
	});

	// Select several items → status / ownership / group / delete (roadmap #17).
	const bulk = bulkSelect({ mediaType: "movie" });

	const toolbar = el(
		"div.toolbar",
		{},
		el(
			"div.toolbar__row",
			{},
			el("label.toolbar__sort-wrap", {}, el("span", {}, "Sort: "), sortSelect),
			el("label.toolbar__sort-wrap", {}, el("span", {}, "Genre: "), genreSelect),
			groupBtn,
			viewBtn,
			bulk.button,
			filter.button,
		),
		filter.panel,
		filter.chips,
	);

	const listContainer = el("div.games-list", {});
	bulk.attach(listContainer);
	const view = el("div.view", {}, header, toolbar, listContainer);
	root.append(view);

	let allMovies = [];
	let rankIndex = new Map();
	let groupCards = [];
	let collapsedIds = new Set();

	function refreshGenreOptions() {
		const tags = new Set();
		for (const m of allMovies) for (const t of tagsOf(m)) tags.add(t);
		const sorted = [...tags].sort((a, b) => a.localeCompare(b));
		// Drop a stale selection that no longer exists in the library.
		if (state.genre && !tags.has(state.genre)) state.genre = "";
		clear(genreSelect);
		genreSelect.append(
			el("option", { value: "", selected: state.genre === "" }, "All genres"),
			...sorted.map((t) =>
				el("option", { value: t, selected: state.genre === t }, t),
			),
		);
	}

	function renderList() {
		clear(listContainer);

		const openGroup = (group) => {
			saveScroll(SCROLL_KEY);
			navigate(`/groups/${group.id}`);
		};
		const openMovie = (movie) => {
			saveScroll(SCROLL_KEY);
			navigate(`/movies/${movie.id}`);
		};

		// The toggle is only meaningful when the category actually has groups.
		groupBtn.hidden = groupCards.length === 0;

		// Ungrouped view: dissolve the groups — show no group cards and collapse
		// nothing, so every member surfaces as its own item.
		const activeGroups = state.expandGroups ? [] : groupCards;
		const activeCollapsed = state.expandGroups ? EMPTY_IDS : collapsedIds;

		const filters = filter.getFilters();
		const filteredItems = allMovies.filter(
			(m) =>
				!activeCollapsed.has(m.id) &&
				matchesAllFilters(m, filters) &&
				(!state.genre || tagsOf(m).includes(state.genre)),
		);
		// Groups sort inline among the films by the active sort (each group ranks
		// as its leading member).
		const entries = sortedEntries({
			items: filteredItems,
			groupCards: activeGroups,
			sort: state.sort,
			rankIndex,
			sortFn: sortMovies,
		});

		// Gallery tiles or glass rows (the toolbar toggle); groups render inline in
		// both, and every node carries data-drag-id/kind so drag-to-group works.
		renderCategoryEntries(listContainer, entries, {
			mediaType: "movie",
			view: state.view,
			count: filteredItems.length,
			onOpenItem: openMovie,
			onOpenGroup: openGroup,
			empty: () => emptyState(allMovies.length === 0, !!state.genre),
		});

		const savedY = consumeScroll(SCROLL_KEY);
		if (savedY != null) {
			requestAnimationFrame(() =>
				requestAnimationFrame(() => window.scrollTo(0, savedY)),
			);
		}
	}

	const [movies, ranking, groups, allMedia] = await Promise.all([
		listMovies(),
		getRankingState("movie"),
		listGroups(),
		listAllMedia(),
	]);
	allMovies = movies;
	rankIndex = new Map(ranking.order.map((id, i) => [id, i]));
	({ cards: groupCards, collapsedIds } = categoryGroups(groups, allMedia, "movie"));
	refreshGenreOptions();

	async function refreshGroups() {
		const fresh = await listGroups();
		({ cards: groupCards, collapsedIds } = categoryGroups(fresh, allMedia, "movie"));
		renderList();
	}
	enableRowDrag(listContainer, {
		onDrop: (payload) => handleRowDrop(payload, { allMedia, refresh: refreshGroups }),
	});

	renderList();
}

function sortMovies(movies, sort, rankIndex) {
	const copy = movies.slice();
	switch (sort) {
		case "title":
			return copy.sort((a, b) => a.title.localeCompare(b.title));
		case "release_year":
			return copy.sort(
				(a, b) => (b.release_year ?? 0) - (a.release_year ?? 0),
			);
		case "last_engaged_date":
			return copy.sort((a, b) =>
				(b.last_engaged_date ?? "").localeCompare(a.last_engaged_date ?? ""),
			);
		case "rating":
			return copy.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
		case "battle_rank":
			return copy.sort((a, b) => {
				const ra = rankIndex.has(a.id) ? rankIndex.get(a.id) : Infinity;
				const rb = rankIndex.has(b.id) ? rankIndex.get(b.id) : Infinity;
				if (ra !== rb) return ra - rb;
				return a.title.localeCompare(b.title);
			});
		case "created_at":
		default:
			return copy.sort((a, b) =>
				(b.created_at ?? "").localeCompare(a.created_at ?? ""),
			);
	}
}

function emptyState(noneAtAll, genreActive) {
	return el(
		"div.empty",
		{},
		el(
			"p",
			{},
			noneAtAll
				? "No movies yet. Tap + Add to find one."
				: genreActive
					? "No movies match this genre + filters."
					: "No movies match these filters.",
		),
	);
}
